import type { InputBaseComponentProps } from "@mui/material/InputBase";
import { omit } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { forwardRef } from "react";
import {
  ConditionSubjectCategoryLine,
  ConditionSubjectValueLine,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/components";

export interface ConditionSubjectLabelProps extends InputBaseComponentProps {
  /**
   * Labels of the whole selected path, from the root option down to the selected one.
   */
  labelPath: string[];
  placeholder?: string;
}

/**
 * Props InputBase hands to its input slot that only a real input element understands.
 */
const inputOnlyProps = ["readOnly", "required", "disabled", "type", "value", "defaultValue", "rows"];

/**
 * Input slot of the subject select showing the selected path on two lines: the parent category on
 * the first one, the subject itself on the second one. An option without a parent renders as a
 * single, vertically centered line.
 */
export const ConditionSubjectLabel = forwardRef<HTMLDivElement, ConditionSubjectLabelProps>(
  ({ labelPath, placeholder, ...props }, ref) => {
    const [categoryLabel, subjectLabel] = labelPath.length > 1 ? labelPath.slice(-2) : [null, labelPath[0] ?? null];

    return (
      <div ref={ref} role="combobox" tabIndex={0} {...omit(inputOnlyProps, props)}>
        {isNotNil(categoryLabel) && <ConditionSubjectCategoryLine>{categoryLabel}</ConditionSubjectCategoryLine>}
        <ConditionSubjectValueLine isPlaceholder={subjectLabel === null}>
          {subjectLabel ?? placeholder}
        </ConditionSubjectValueLine>
      </div>
    );
  }
);
ConditionSubjectLabel.displayName = "ConditionSubjectLabel";
